// platform-mfa-totp: אפליקציית אימות — מסך הקוד בכניסה (6 ספרות, שליחה אוטומטית, הדבקה), ההצטרפות (QR + מפתח ידני → קוד → הנחיות
// שחזור), השער שעוטף את האפליקציה (RequireSession → MfaGate → Layout) והבאנר בימי החסד. הגורמים אצל GoTrue; מה צריך — מה-api.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { logout, useSession } from '../lib/auth.ts';
import { setOnMfaRequired } from '../lib/api.ts';
import { useBrand } from '../lib/brand.ts';
import { chooseLater, codeComplete, enrollTotp, fetchMfaStatus, graceDateHe, laterChosen, listFactors, mfaStatusFor, normalizeCode, qrSrc, setMfaStatus, unenroll, useMfaStatus, verifyTotp, type Enrollment, type MfaStatus, type VerifyResult } from '../lib/mfa.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Input, Label } from './ui/input.tsx';
import { Logo } from './brand/Logo.tsx';
import { IconAlert, IconCheck, IconShield } from './brand/icons.tsx';
import { Loading } from './Status.tsx';

const ERR: Record<Exclude<VerifyResult, 'ok'>, string> = { wrong: T.mfa.code.wrong, rate: T.mfa.code.rate, failed: T.mfa.code.failed };

/** שדה קוד: ספרות בלבד, עד 6; כשמלא — onSubmit פעם אחת (עד שהערך משתנה); הדבקה של "123 456" מתנקה; שגיאה מרוקנת ומחזירה פוקוס. */
export function CodeInput({ onSubmit, busy, error, autoFocus = true, id = 'mfa-code' }: { onSubmit: (code: string) => void; busy: boolean; error: string; autoFocus?: boolean; id?: string }) {
  const [code, setCode] = useState('');
  const sent = useRef<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (codeComplete(code) && sent.current !== code && !busy) {
      sent.current = code;
      onSubmit(code);
    }
  }, [code, busy, onSubmit]);
  useEffect(() => {
    if (error) {
      setCode('');
      sent.current = null;
      ref.current?.focus();
    }
  }, [error]);
  return (
    <form
      className="flex flex-col gap-2"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (codeComplete(code) && !busy) {
          sent.current = code;
          onSubmit(code);
        }
      }}
    >
      <Label htmlFor={id}>{T.mfa.code.label}</Label>
      <Input
        id={id}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        dir="ltr"
        autoFocus={autoFocus}
        disabled={busy}
        value={code}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className="text-center text-2xl tracking-[0.5em] font-semibold tabular-nums"
        onChange={(e) => setCode(normalizeCode(e.target.value))}
        onPaste={(e) => {
          e.preventDefault();
          setCode(normalizeCode(e.clipboardData.getData('text')));
        }}
        data-testid="mfa-code"
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="m-0 flex items-center gap-2 rounded-sm border border-error/35 bg-error/[0.07] px-3 py-2 text-sm font-medium text-error" data-testid="mfa-error">
          <IconAlert size={16} />
          {error}
        </p>
      )}
      {busy && <p className="m-0 text-sm text-muted-foreground" role="status">{T.mfa.code.busy}</p>}
    </form>
  );
}

function Screen({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <main className="grid min-h-(--ab-screen) place-items-center bg-background px-4 py-8 text-foreground">
      <div className="flex w-full max-w-[420px] flex-col gap-6 rounded-md border border-border bg-card p-6 shadow-card" data-testid={testId}>
        <Logo className="h-9 self-start" />
        {children}
      </div>
    </main>
  );
}

/** הכניסה: יש גורם, הסשן aal1 → קוד. הצלחה = GoTrue החליף את הסשן (aal2); השער רואה סשן חדש ומרענן את המצב. */
export function MfaCodeScreen({ factorId, onOk }: { factorId: string; onOk: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (code: string) => {
    setBusy(true);
    setErr('');
    const r = await verifyTotp(factorId, code);
    setBusy(false);
    if (r === 'ok') onOk();
    else setErr(ERR[r]);
  };
  return (
    <Screen testId="mfa-code-screen">
      <div className="flex flex-col gap-2">
        <h1 className="m-0 flex items-center gap-2 text-xl font-bold">
          <IconShield size={22} />
          {T.mfa.code.title}
        </h1>
        <p className="m-0 text-[15px] text-muted-foreground">{T.mfa.code.lead}</p>
      </div>
      <CodeInput onSubmit={(c) => void submit(c)} busy={busy} error={err} />
      <div className="flex items-center justify-between gap-3 text-[13px] text-muted-foreground">
        <span>{T.mfa.code.lost}</span>
        <button type="button" className="shrink-0 cursor-pointer text-link hover:underline" onClick={() => logout()} data-testid="mfa-logout">
          {T.mfa.code.logout}
        </button>
      </div>
    </Screen>
  );
}

export type EnrollMode = 'required' | 'optional' | 'settings' | 'replace';

/**
 * הצטרפות / החלפת מכשיר. required: בלי "אחר כך". optional (בימי החסד): "אחר כך". settings/replace: מתוך מסך ההגדרות.
 * replace: אחרי שהגורם החדש אומת — הישנים מוסרים (הסשן כבר aal2 אחרי האימות).
 */
export function MfaEnroll({ mode, graceUntil, onDone, onLater, onCancel }: { mode: EnrollMode; graceUntil?: string | null; onDone: () => void; onLater?: () => void; onCancel?: () => void }) {
  const brand = useBrand();
  const [step, setStep] = useState<'intro' | 'scan' | 'done'>(mode === 'settings' || mode === 'replace' ? 'scan' : 'intro');
  const [enr, setEnr] = useState<Enrollment | null>(null);
  const [startErr, setStartErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (step !== 'scan' || started.current) return;
    started.current = true;
    const issuer = `aibify · ${brand?.nameHe || window.location.hostname}`;
    void enrollTotp(issuer).then((r) => {
      if (typeof r === 'string') setStartErr(r === 'disabled' ? T.mfa.enroll.disabled : r === 'aal2' ? T.mfa.enroll.needCode : T.mfa.enroll.startFailed);
      else setEnr(r);
    });
  }, [step, brand]);

  const submit = async (code: string) => {
    if (!enr) return;
    setBusy(true);
    setErr('');
    const r = await verifyTotp(enr.factorId, code);
    if (r !== 'ok') {
      setBusy(false);
      setErr(ERR[r]);
      return;
    }
    if (mode === 'replace') {
      const old = (await listFactors().catch(() => [])).filter((f) => f.id !== enr.factorId);
      for (const f of old) await unenroll(f.id);
    }
    setBusy(false);
    setStep('done');
  };

  const lead = mode === 'required' ? T.mfa.enroll.leadRequired : mode === 'optional' && graceUntil ? T.mfa.enroll.leadGrace(graceDateHe(graceUntil)) : T.mfa.enroll.lead;
  return (
    <div className="flex flex-col gap-5" data-testid="mfa-enroll" data-step={step} data-mode={mode}>
      {step === 'intro' && (
        <>
          <div className="flex flex-col gap-2">
            <h1 className="m-0 flex items-center gap-2 text-xl font-bold">
              <IconShield size={22} />
              {mode === 'required' ? T.mfa.enroll.titleRequired : T.mfa.enroll.title}
            </h1>
            <p className="m-0 text-[15px] text-muted-foreground">{lead}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="lg" onClick={() => setStep('scan')} data-testid="mfa-start">
              {T.mfa.enroll.start}
            </Button>
            {mode === 'optional' && onLater && (
              <Button size="lg" variant="outline" onClick={onLater} data-testid="mfa-later">
                {T.mfa.enroll.later}
              </Button>
            )}
          </div>
        </>
      )}
      {step === 'scan' && (
        <>
          <h2 className="m-0 text-lg font-bold">{mode === 'replace' ? T.mfa.enroll.replace : T.mfa.enroll.title}</h2>
          {startErr && (
            <p role="alert" className="m-0 flex items-center gap-2 rounded-sm border border-error/35 bg-error/[0.07] px-3 py-2 text-sm font-medium text-error" data-testid="mfa-start-error">
              <IconAlert size={16} />
              {startErr}
            </p>
          )}
          {!enr && !startErr && <Loading />}
          {enr && (
            <>
              <ol className="m-0 flex list-none flex-col gap-4 p-0">
                <li className="flex flex-col gap-2">
                  <b className="text-sm">1. {T.mfa.enroll.step1}</b>
                  <img src={qrSrc(enr.qrSvg)} alt="" width={200} height={200} className="self-center rounded-sm bg-white p-2" data-testid="mfa-qr" />
                  <p className="m-0 text-[13px] text-muted-foreground">{T.mfa.enroll.manual}</p>
                  <code dir="ltr" className="select-all break-all rounded-sm bg-muted px-2 py-1.5 text-center text-sm tracking-wider" data-testid="mfa-secret">
                    {enr.secret.replace(/(.{4})/g, '$1 ').trim()}
                  </code>
                </li>
                <li className="flex flex-col gap-2">
                  <b className="text-sm">2. {T.mfa.enroll.step2}</b>
                  <CodeInput onSubmit={(c) => void submit(c)} busy={busy} error={err} autoFocus={false} id="mfa-enroll-code" />
                </li>
              </ol>
            </>
          )}
          {onCancel && (
            <Button variant="ghost" className="self-start" onClick={onCancel} data-testid="mfa-cancel">
              {T.users.form.cancel}
            </Button>
          )}
        </>
      )}
      {step === 'done' && (
        <>
          <div className="flex flex-col gap-2">
            <h2 className="m-0 flex items-center gap-2 text-lg font-bold text-[hsl(var(--state-ok))]">
              <IconCheck size={22} />
              {T.mfa.enroll.done}
            </h2>
            <p className="m-0 text-[15px] text-muted-foreground">{T.mfa.enroll.doneLead}</p>
          </div>
          <div className="flex flex-col gap-1.5 rounded-md border border-degraded/50 bg-degraded/[0.08] p-3 text-sm" data-testid="mfa-recovery">
            <b>{T.mfa.enroll.recoveryTitle}</b>
            <ul className="m-0 flex list-disc flex-col gap-1 ps-5">
              {T.mfa.enroll.recovery.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </div>
          <Button size="lg" className="self-start" onClick={onDone} data-testid="mfa-continue">
            {T.mfa.enroll.continue}
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * השער: אחרי RequireSession ולפני המעטפת. מצב מה-api לכל access token (קוד / רענון מחליפים טוקן → טעינה מחדש של המצב).
 * verify → מסך הקוד; enroll → הצטרפות חובה; enroll_optional → הצטרפות עם "אחר כך" (ללשונית), ואז הבאנר ב-Layout.
 */
export function MfaGate({ children }: { children: ReactNode }) {
  const s = useSession();
  const token = s?.accessToken ?? '';
  const status = useMfaStatus();
  const valid = token ? mfaStatusFor(token) : null;
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!token || valid) return;
    let live = true;
    setFailed(false);
    fetchMfaStatus()
      .then((st) => live && setMfaStatus(st, token))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [token, valid, tick]);
  // קריאה אחרת קיבלה 401 mfa_* (למשל: admin איפס את הגורם בזמן שהמסך פתוח) → המצב נטען מחדש במקום טעינת דף
  useEffect(() => {
    setOnMfaRequired(() => setMfaStatus(null, null));
    return () => setOnMfaRequired(null);
  }, []);

  if (!s) return null;
  if (failed)
    return (
      <Screen testId="mfa-failed">
        <p className="m-0 text-[15px]">{T.network}</p>
        <Button variant="outline" className="self-start" onClick={() => setTick((n) => n + 1)}>
          {T.retry}
        </Button>
      </Screen>
    );
  if (!valid || !status) return <Loading variant="screen" />;
  const reload = () => setMfaStatus(null, null);
  if (status.need === 'verify' && status.factorId) return <MfaCodeScreen factorId={status.factorId} onOk={reload} />;
  if (status.need === 'enroll' || (status.need === 'enroll_optional' && !laterChosen(s.user.id)))
    return (
      <Screen testId="mfa-enroll-screen">
        <MfaEnroll
          mode={status.need === 'enroll' ? 'required' : 'optional'}
          graceUntil={status.graceUntil}
          onDone={reload}
          onLater={() => {
            chooseLater(s.user.id);
            setTick((n) => n + 1);
          }}
        />
        <button type="button" className="self-start cursor-pointer text-[13px] text-muted-foreground hover:underline" onClick={() => logout()} data-testid="mfa-logout">
          {T.mfa.code.logout}
        </button>
      </Screen>
    );
  return <>{children}</>;
}

/** בימי החסד, אחרי "אחר כך": פס דק מעל התוכן עם התאריך וקישור להגדרות. */
export function MfaBanner({ className }: { className?: string }) {
  const status = useMfaStatus();
  if (!status || status.need !== 'enroll_optional' || !status.graceUntil) return null;
  return (
    <div role="status" className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-degraded/50 bg-degraded/[0.1] px-4 py-2 text-sm', className)} data-testid="mfa-banner">
      <IconShield size={16} />
      <span>{T.mfa.banner(graceDateHe(status.graceUntil))}</span>
      <Link to="/settings/security" className="font-semibold text-link hover:underline">
        {T.mfa.bannerAct}
      </Link>
    </div>
  );
}

export const mfaStatusLabel = (st: MfaStatus | null) => (st?.enrolled ? T.mfa.settings.on : T.mfa.settings.off);
